import * as React from 'react';
import * as _ from 'lodash';
import * as routes from '../../App/routeConstants';
import {createSelector} from 'reselect';
import GavelIcon from '@material-ui/icons/Gavel';
import AddIcon from '@material-ui/icons/Add';
import PaletteIcon from '@material-ui/icons/Palette';
import SettingsIcon from '@material-ui/icons/Settings';
import PhotoIcon from '@material-ui/icons/Photo';
import HomeIcon from '@material-ui/icons/Home';
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';
import {accountPageLeftMenuOptions} from '../../MyAccount/AccountPage/AccountPage';
import {STATUS_FILTER_ACTIVE} from '../../MyAccount/Listings/consts';
import {
  Profile,
  EBayAccounts,
  Settings,
  AmazonAccounts,
  AccountPageEntry,
  AccountPageEntryProps,
  PlanAndBilling,
} from '../../MyAccount/AccountPage/AccountPageSectionsData';
import PackageVariant from 'mdi-material-ui/PackageVariant';
import PackageVariantClosed from 'mdi-material-ui/PackageVariantClosed';
import ClipboardTextOutline from 'mdi-material-ui/ClipboardTextOutline';
import Shape from 'mdi-material-ui/Shape';
import Amazon from 'mdi-material-ui/Amazon';
import {Location} from 'history';
import {CombinedNavigationTree, Path, NavigationTree, NavigationTreeEntry, SelectedEntries} from './ApplicationLayout';
import {AMAZON_STATUS_FILTER_ACTIVE} from '../../MyAccount/AmazonListings/consts';
import SvgIcon from 'cl-icons/SvgIcon';
import {getItemId, isNonDraft, getEbayListing} from '../../MyAccount/Listings/EbayListing/ebayListingSelectors';
import {getProductFormValues} from '../../MyAccount/Inventory/EditProductSelectors';
import {FIELD_NAMES} from '../../MyAccount/Listings/EbayListing/consts';

const settingsSelectedPaths = [routes.MyAccount.Upgrade, routes.MyAccount.ClearDescription];
const productsSelectedPaths = [
  routes.MyAccount.Inventory,
  routes.MyAccount.Import,
  routes.MyAccount.PublishToEbay,
  routes.MyAccount.PublishToAmazon,
];
const myTemplatesSelectedPaths = [routes.MyAccount.ViewTemplatesBase, routes.MyAccount.ViewDesigns];

const getReferenceTransactionEnabled = state => state.user.referenceTransactionEnabled;
const getReferralProgram = state => state.user.referrals.referralProgram;
export const getEbayAccounts = state => state.accountPage.accounts;
export const getAmazonAccounts = state => state.accountPage.amazonAccounts;
// the field in store called eBayAccountLimit due to legacy reasons - it is for ALL channels.
export const getAccountsLimit = state => state.user.userPlan.eBayAccountsLimit;
export const getLastAmazonAccountsError = state => state.accountPage.lastAmazonError;
export const getCurrentLocation = state => state.routing.location;
export const getAmazonSellerNameById = (state, sellerId) =>
  sellerId ? _.get(getAmazonAccounts(state)[sellerId], 'name') : undefined;

export const getAccountsConnected = createSelector(
  [getEbayAccounts, getAmazonAccounts],
  (ebayAccounts, amazonAccounts) => _.size(ebayAccounts) + _.size(amazonAccounts)
);

const isEntryLocationEqualsCurrentLocation = (entry, currentLocation: Location) => {
  if (_.isFunction(entry.isEntrySelected)) {
    return entry.isEntrySelected(currentLocation);
  }
  // TODO maybe in the future more location elements should be compared, for now pathname is enough
  return entry.location && entry.location.pathname === currentLocation.pathname;
};

const getEbayListingText = createSelector(
  [getItemId, isNonDraft, getEbayListing],
  (itemId, nonDraft, ebayListing) => {
    if (itemId) {
      if (nonDraft) {
        return ebayListing && ebayListing[FIELD_NAMES.EBAY_TITLE]
          ? `${itemId} - ${ebayListing[FIELD_NAMES.EBAY_TITLE]}`
          : `${itemId}`;
      }
      return 'Draft listing';
    }
    return 'New listing';
  }
);

const getEbayNavigationTreeEntry = createSelector(
  [getEbayAccounts, getEbayListingText],
  (ebayAccounts, ebayListingText): NavigationTreeEntry => {
    const ebayAccountsEntries: CombinedNavigationTree = {};
    _.forEach(ebayAccounts, (ebayAccount, accountName) => {
      ebayAccountsEntries[accountName] = {
        text: accountName,
        location: {
          pathname: routes.MyAccount.Listings,
          query: {
            selectedSellerId: accountName,
            statusFilter: STATUS_FILTER_ACTIVE,
          },
        },
        isEntrySelected: (currentLocation: Location) =>
          (currentLocation.pathname === routes.MyAccount.Listings ||
            _.includes(currentLocation.pathname, routes.MyAccount.EbayListingDataFetcher)) &&
          currentLocation.query.selectedSellerId === accountName,
        hideFromSideMenu: true, //as we currently support only two-level-list, this prop is mandatory.
        children: {
          editListing: {
            text: ebayListingText,
            location: {pathname: routes.MyAccount.EbayListingDataFetcher},
            isEntrySelected: (currentLocation: Location) =>
              _.includes(currentLocation.pathname, routes.MyAccount.EbayListing),
          },
          findProduct: {
            text: 'eBay catalog',
            location: {pathname: routes.MyAccount.FindEbayProduct},
            isEntrySelected: (currentLocation: Location) =>
              _.includes(currentLocation.pathname, routes.MyAccount.FindEbayProduct),
          },
        },
      };
    });
    return {
      icon: <GavelIcon />,
      text: 'eBay',
      children: {
        ebayAccounts: {
          scrollableEntries: ebayAccountsEntries,
        },
        addEbayAccount: {
          icon: <AddIcon />,
          text: 'Add account',
          location: {pathname: EBayAccounts.route},
          divider: true,
          isEntrySelected: () => false,
        },
        myTemplates: {
          icon: <PaletteIcon />,
          text: 'My templates',
          location: {pathname: routes.MyAccount.ViewTemplatesBase},
          isEntrySelected: (currentLocation: Location) =>
            _.includes(currentLocation.pathname, routes.MyAccount.ViewTemplatesBase) ||
            _.includes(myTemplatesSelectedPaths, currentLocation.pathname),
          hideFromSideMenu: true,
          children: {
            newTemplateDesign: {
              text: 'New template design',
              location: {pathname: routes.MyAccount.ViewDesigns},
            },
          },
        },
        templatesImages: {
          icon: <PhotoIcon />,
          text: 'Template images',
          location: {pathname: routes.MyAccount.AssetManagerBase},
          isEntrySelected: (currentLocation: Location) =>
            _.includes(currentLocation.pathname, routes.MyAccount.AssetManagerBase),
        },
        eBaySettings: {
          icon: <SettingsIcon />,
          text: 'eBay settings',
          location: {pathname: Settings.route},
          isEntrySelected: () => false,
        },
      },
    };
  }
);

const getAmazonNavigationTreeEntry = createSelector(
  [getAmazonAccounts],
  (amazonAccounts): NavigationTreeEntry => {
    const amazonAccountsEntries: CombinedNavigationTree = {};
    _.forEach(amazonAccounts, ({amazonSellerId, name}) => {
      amazonAccountsEntries[amazonSellerId] = {
        text: name,
        location: {
          pathname: routes.MyAccount.AmazonListings,
          query: {
            selectedSellerId: amazonSellerId,
            statusFilter: AMAZON_STATUS_FILTER_ACTIVE,
          },
        },
        isEntrySelected: (currentLocation: Location) =>
          currentLocation.pathname === routes.MyAccount.AmazonListings &&
          currentLocation.query.selectedSellerId === amazonSellerId,
      };
    });
    return {
      icon: <Amazon />,
      text: 'Amazon',
      children: {
        amazonAccounts: {
          scrollableEntries: amazonAccountsEntries,
        },
        addAmazonAccount: {
          icon: <AddIcon />,
          text: 'Add account',
          location: {pathname: AmazonAccounts.route},
          divider: true,
          isEntrySelected: () => false,
        },
      },
    };
  }
);

const getAccountPageEntryProps = createSelector(
  [getReferenceTransactionEnabled, getReferralProgram],
  (referenceTransactionEnabled, referralProgram) =>
    ({referenceTransactionEnabled, referralProgram} as AccountPageEntryProps)
);

const getSelectSellerId = state => state.routing.location.query.selectedSellerId;
const getAmazonSellerName = state => getAmazonSellerNameById(state, getSelectSellerId(state));
const getSelectedPlan = state => {
  const plans = _.keyBy(state.user.plans, 'id');
  return _.get(plans[state.routing.location.query.plan], 'name');
};

export const getNavigationTree = createSelector(
  [
    getEbayNavigationTreeEntry,
    getAmazonNavigationTreeEntry,
    getAccountPageEntryProps,
    getProductFormValues,
    getSelectSellerId,
    getAmazonSellerName,
    getSelectedPlan,
  ],
  (
    ebayNavigationTreeEntry,
    amazonNavigationTreeEntry,
    accountPageEntryProps,
    productFormValues,
    selectedSellerId,
    amazonSellerName,
    selectedPlan
  ): NavigationTree => {
    let editProductText;
    if (productFormValues) {
      if (!_.isEmpty(productFormValues['Title'])) {
        editProductText = `${productFormValues['SKU']} - ${productFormValues['Title']}`;
      } else {
        editProductText = productFormValues['SKU'];
      }
    }
    return {
      top: {
        dashboard: {
          icon: <HomeIcon />,
          text: 'Home',
          location: {pathname: routes.MyAccount.Dashboard},
        },
        products: {
          icon: <PackageVariant />,
          text: 'Products',
          children: {
            productsCatalog: {
              icon: <PackageVariantClosed />,
              text: 'Products catalog',
              location: {pathname: routes.MyAccount.Inventory},
              isEntrySelected: (currentLocation: Location) =>
                _.includes(currentLocation.pathname, routes.MyAccount.ProductBase) ||
                _.includes(productsSelectedPaths, currentLocation.pathname),
              hideFromSideMenu: true,
              children: {
                publishToEbay: {
                  text: `Publish products to ${selectedSellerId}`,
                  location: {pathname: routes.MyAccount.PublishToEbay},
                },
                publishToAmazon: {
                  text: `Publish products to ${amazonSellerName}`,
                  location: {pathname: routes.MyAccount.PublishToAmazon},
                },
                editProduct: {
                  text: editProductText,
                  isEntrySelected: (currentLocation: Location) =>
                    _.includes(currentLocation.pathname, routes.MyAccount.ProductBase) &&
                    routes.MyAccount.ProductBase !== currentLocation.pathname,
                },
                newProduct: {
                  text: 'New product',
                  location: {pathname: routes.MyAccount.ProductBase},
                },
                import: {
                  text: 'Imports',
                  location: {pathname: routes.MyAccount.Import},
                },
              },
            },
            inventory: {
              icon: <ClipboardTextOutline />, // TODO Semi needs to get a new icon since Package doesn't work
              text: 'Inventory',
              location: {pathname: routes.MyAccount.InventoryManagement},
            },
            attributes: {
              icon: <Shape />,
              text: 'Attributes',
              location: {pathname: routes.MyAccount.Attributes},
            },
          },
        },
        ebay: ebayNavigationTreeEntry,
        amazon: amazonNavigationTreeEntry,
      },
      bottom: {
        settings: {
          icon: <SettingsIcon />,
          text: 'Settings',
          location: {pathname: Profile.route},
          isEntrySelected: (currentLocation: Location) =>
            _.includes(currentLocation.pathname, routes.MyAccount.AccountPageBase) ||
            _.includes(settingsSelectedPaths, currentLocation.pathname),
          children: _.mapValues<typeof accountPageLeftMenuOptions, NavigationTreeEntry>(
            accountPageLeftMenuOptions,
            (entry: AccountPageEntry, key): NavigationTreeEntry => {
              if (entry) {
                return {
                  text: entry.getLabel ? entry.getLabel(accountPageEntryProps) : entry.label || key,
                  location: {pathname: entry.getRoute ? entry.getRoute(accountPageEntryProps) : entry.route},
                  icon: <SvgIcon path={entry.iconPath} />,
                  ...(key === Settings.label && {
                    isEntrySelected: (currentLocation: Location) =>
                      entry.route === currentLocation.pathname ||
                      _.includes(currentLocation.pathname, routes.MyAccount.ClearDescription),
                    hideFromSideMenu: true,
                    children: {
                      clearDescription: {
                        text: `Description cleaner - ${selectedSellerId}`,
                        location: {pathname: routes.MyAccount.ClearDescription},
                        isEntrySelected: (currentLocation: Location) =>
                          _.includes(currentLocation.pathname, routes.MyAccount.ClearDescription),
                      },
                    },
                  }),
                  ...(key === PlanAndBilling.label && {
                    isEntrySelected: (currentLocation: Location) =>
                      entry.route === currentLocation.pathname ||
                      _.includes(currentLocation.pathname, routes.MyAccount.Upgrade),
                    hideFromSideMenu: true,
                    children: {
                      switchPlan: {
                        text: 'Pricing plans',
                        location: {pathname: routes.MyAccount.Upgrade},
                        isEntrySelected: (currentLocation: Location) =>
                          _.includes(currentLocation.pathname, routes.MyAccount.Upgrade),
                        children: {
                          selectedPlan: {
                            text: _.toString(selectedPlan),
                            isEntrySelected: (currentLocation: Location) =>
                              _.includes(currentLocation.pathname, `${routes.MyAccount.Upgrade}`) &&
                              _.includes(currentLocation.search, 'plan='),
                          },
                        },
                      },
                    },
                  }),
                };
              } else {
                return {
                  text: key,
                };
              }
            }
          ),
        },
        logout: {
          icon: <PowerSettingsNewIcon />,
          text: 'Logout',
          href: routes.Auth.Logout,
        },
      },
    };
  }
);

export const getCombinedNavigationTree = createSelector(
  [getNavigationTree],
  (navigationTree): CombinedNavigationTree => ({...navigationTree.top, ...navigationTree.bottom})
);

export const flattenScrollableEntries = (navigationTree: CombinedNavigationTree): CombinedNavigationTree => {
  let flatNavigationTree = {};
  _.forEach(navigationTree, (entry: NavigationTreeEntry, key: string) => {
    if (_.isEmpty(entry.scrollableEntries)) {
      flatNavigationTree[key] = entry;
    } else {
      flatNavigationTree = {...flatNavigationTree, ...entry.scrollableEntries};
    }
  });
  return flatNavigationTree;
};

const getSelectedEntriesRecursive = (
  combinedNavigationTree: CombinedNavigationTree,
  currentLocation: Location,
  firstLevel = true
): Path[] => {
  let selectedEntries: Path[] = [];
  _.forEach(combinedNavigationTree, (entry, key) => {
    let entryChildSelected = false;
    if (!_.isEmpty(entry.children)) {
      _.forEach(flattenScrollableEntries(entry.children), (childEntry: NavigationTreeEntry, childEntryKey: string) => {
        if (isEntryLocationEqualsCurrentLocation(childEntry, currentLocation)) {
          const childEntrySelectedEntryKey = {[key]: childEntryKey};
          selectedEntries.push(childEntrySelectedEntryKey);
          entryChildSelected = true;
        }
      });
      if (entryChildSelected) {
        if (firstLevel) {
          selectedEntries.push({root: key});
        }
        selectedEntries = [
          ...getSelectedEntriesRecursive(flattenScrollableEntries(entry.children), currentLocation, false),
          ...selectedEntries,
        ];
      }
    } else if (isEntryLocationEqualsCurrentLocation(entry, currentLocation) && firstLevel) {
      selectedEntries.push({root: key});
    }
  });
  return selectedEntries;
};

export const getSortedSelectedEntries = createSelector(
  [getCombinedNavigationTree, getCurrentLocation],
  (combinedNavigationTree, currentLocation: Location): Path[] => {
    return getSelectedEntriesRecursive(combinedNavigationTree, currentLocation);
  }
);

export const getSelectedEntries = createSelector(
  [getSortedSelectedEntries],
  (selectedEntries): SelectedEntries => {
    const result = !_.isEmpty(selectedEntries) ? {[_.last(selectedEntries)['root']]: true} : {};
    return _.reduce(
      _.dropRight(selectedEntries),
      (entries, entry) => {
        const [from, to] = _.toPairs(entry)[0];
        return {...entries, [`${from}.${to}`]: true};
      },
      result
    );
  }
);
